import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrg } from '../../components/OrgLayout'
import { supabase } from '../../lib/supabase'
import {
  VStack,
  HStack,
  Card,
  StatCard,
  Grid,
  Text,
  Badge,
  Button,
  Animate,
  Avatar,
  Heading
} from 'wss3-forge'
import {
  People20Regular,
  Globe20Regular,
  Key20Regular,
  Checkmark20Regular,
  Warning20Regular
} from '@fluentui/react-icons'

interface OrgStats {
  total_members: number
  total_domains: number
  verified_domains: number
  active_sso_connections: number
  sso_logins_30d: number
}

export function OrgDashboard() {
  const navigate = useNavigate()
  const { organization } = useOrg()
  const [stats, setStats] = useState<OrgStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [organization.id])

  const loadStats = async () => {
    try {
      const { data, error } = await supabase.rpc('get_org_stats', {
        p_organization_id: organization.id
      })

      if (error) throw error
      setStats(data)
    } catch (err) {
      console.error('Error loading stats:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <HStack gap="md" style={{ alignItems: 'center' }}>
          <Avatar
            name={organization.name}
            src={organization.logo_url || undefined}
            size="lg"
          />
          <Heading level={2}>{organization.name}</Heading>
        </HStack>
      </Animate>

      <Animate type="slideInUp" delay={50}>
        <Grid columns={{ xs: 1, sm: 2, lg: 4 }} gap="md">
          <StatCard
            label="Members"
            value={loading ? '-' : stats?.total_members || 0}
            icon={<People20Regular />}
            color="var(--brand-primary)"
            onClick={() => navigate(`/org/${organization.slug}/members`)}
          />
          <StatCard
            label="Domains"
            value={loading ? '-' : `${stats?.verified_domains || 0}/${stats?.total_domains || 0}`}
            subtitle="verified"
            icon={<Globe20Regular />}
            color="#10B981"
            onClick={() => navigate(`/org/${organization.slug}/domains`)}
          />
          <StatCard
            label="SSO Connections"
            value={loading ? '-' : stats?.active_sso_connections || 0}
            subtitle="active"
            icon={<Key20Regular />}
            color="#F59E0B"
            onClick={() => navigate(`/org/${organization.slug}/sso`)}
          />
          <StatCard
            label="SSO Logins"
            value={loading ? '-' : stats?.sso_logins_30d || 0}
            subtitle="last 30 days"
            icon={<People20Regular />}
            color="#6366F1"
          />
        </Grid>
      </Animate>

      <Animate type="slideInUp" delay={100}>
        <Card padding="lg" title="Quick Actions">
          <Grid columns={{ xs: 1, md: 3 }} gap="md">
            <Card
              hoverable
              padding="md"
              onClick={() => navigate(`/org/${organization.slug}/sso`)}
            >
              <VStack gap="sm" style={{ alignItems: 'flex-start' }}>
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  <Key20Regular style={{ color: '#F59E0B' }} />
                  <Text weight="semibold">Configure SSO</Text>
                </HStack>
                <Text size="sm" color="muted">Add a SAML or OIDC connection for an application</Text>
                {stats?.active_sso_connections ? (
                  <HStack gap="xs" style={{ alignItems: 'center' }}>
                    <Checkmark20Regular style={{ color: '#10B981', fontSize: '16px' }} />
                    <Text size="sm" color="muted">Configured</Text>
                  </HStack>
                ) : (
                  <Badge size="sm" variant="warning">To configure</Badge>
                )}
              </VStack>
            </Card>

            <Card
              hoverable
              padding="md"
              onClick={() => navigate(`/org/${organization.slug}/domains`)}
            >
              <VStack gap="sm" style={{ alignItems: 'flex-start' }}>
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  <Globe20Regular style={{ color: '#10B981' }} />
                  <Text weight="semibold">Verify Domains</Text>
                </HStack>
                <Text size="sm" color="muted">Add and verify your email domains</Text>
                {stats?.verified_domains === stats?.total_domains && (stats?.total_domains || 0) > 0 ? (
                  <HStack gap="xs" style={{ alignItems: 'center' }}>
                    <Checkmark20Regular style={{ color: '#10B981', fontSize: '16px' }} />
                    <Text size="sm" color="muted">All verified</Text>
                  </HStack>
                ) : (
                  <Badge size="sm" variant="warning">To configure</Badge>
                )}
              </VStack>
            </Card>

            <Card
              hoverable
              padding="md"
              onClick={() => navigate(`/org/${organization.slug}/members`)}
            >
              <VStack gap="sm" style={{ alignItems: 'flex-start' }}>
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  <People20Regular style={{ color: 'var(--brand-primary)' }} />
                  <Text weight="semibold">Manage Members</Text>
                </HStack>
                <Text size="sm" color="muted">View users who logged in via SSO</Text>
                <Text size="sm" color="muted">{stats?.total_members || 0} members</Text>
              </VStack>
            </Card>
          </Grid>
        </Card>
      </Animate>

      {stats && stats.verified_domains === 0 && (
        <Animate type="slideInUp" delay={150}>
          <Card padding="lg" variant="outlined">
            <HStack gap="md" style={{ alignItems: 'center' }}>
              <Warning20Regular style={{ color: '#F59E0B', fontSize: '24px' }} />
              <VStack gap="xs" style={{ flex: 1 }}>
                <Text weight="semibold">Setup Incomplete</Text>
                <Text size="sm" color="muted">
                  You need to add and verify at least one domain to enable SSO.
                </Text>
              </VStack>
              <Button
                size="sm"
                onClick={() => navigate(`/org/${organization.slug}/domains`)}
              >
                Configure
              </Button>
            </HStack>
          </Card>
        </Animate>
      )}
    </VStack>
  )
}
